import { useShow, useNavigation } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import {
  Card,
  Descriptions,
  Tag,
  Row,
  Col,
  Button,
  Space,
  Popconfirm,
  message,
  Table,
  QRCode,
  Dropdown,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  PrinterOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import QRCodeLib from 'qrcode';
import { apiClient } from '../../lib/api-client';

interface BusBooking {
  id: number;
  bookingCode: string;
  groupCode: string | null;
  passengerName: string;
  passengerPhone: string;
  passengerPassport: string | null;
  passengerEmail: string | null;
  passengerGender: 'M' | 'F' | null;
  seatNumber: number;
  price: number;
  isPaid: boolean;
  paidAt: string | null;
  paymentMethod: 'CASH' | 'CARD' | null;
  source: 'PROGRAM' | 'KASSA';
  status: string;
  cancelledAt: string | null;
  cancelReason: string | null;
  createdAt: string;
  busTrip: {
    id: number;
    departureRegion: { nameUz: string; nameRu: string; nameEn: string };
    departureCity: { nameUz: string; nameRu: string; nameEn: string };
    arrivalRegion: { nameUz: string; nameRu: string; nameEn: string };
    arrivalCity: { nameUz: string; nameRu: string; nameEn: string };
    departureBusStation: { nameUz: string } | string | null;
    arrivalBusStation: { nameUz: string } | string | null;
    departureTime: string;
    arrivalTime: string;
    pricePerSeat: number;
    busType: string;
    busNumber: string | null;
    busCompany: { id: number; name: string };
  };
  boardingStop?: { city?: { nameUz: string } } | null;
  disembarkStop?: { city?: { nameUz: string } } | null;
}

export const BusBookingShow = () => {
  const { show, list } = useNavigation();
  const { queryResult } = useShow<BusBooking>();
  const { data, isLoading, refetch } = queryResult;
  const booking = data?.data;
  const [pdfLoading, setPdfLoading] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');

  // QR encodes the public ticket-check URL so passengers can verify
  // their ticket by scanning. Origin defaults to the production landing.
  useEffect(() => {
    if (booking?.bookingCode) {
      const url = `https://safaro.uz/bus-ticket-check?code=${encodeURIComponent(
        booking.bookingCode,
      )}`;
      QRCodeLib.toDataURL(url, {
        width: 200,
        margin: 1,
        color: { dark: '#1A1A2E', light: '#ffffff' },
      })
        .then(setQrDataUrl)
        .catch(console.error);
    }
  }, [booking?.bookingCode]);

  const handleConfirmPayment = async (paymentMethod?: 'CASH' | 'CARD') => {
    try {
      await apiClient.post(`/admin/bus/bookings/${booking?.id}/confirm-payment`, {
        paymentMethod,
      });
      message.success(
        paymentMethod
          ? `To'lov tasdiqlandi (${paymentMethod === 'CASH' ? 'Naqd' : 'Karta'})`
          : "To'lov tasdiqlandi",
      );
      refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleCancel = async () => {
    try {
      await apiClient.post(`/admin/bus/bookings/${booking?.id}/cancel`, {
        reason: 'Admin tomonidan bekor qilindi',
      });
      message.success('Buyurtma bekor qilindi');
      refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleDownloadPDF = async () => {
    if (!booking || !qrDataUrl) return;

    // Per-seat shape: one booking = one passenger. Wrap into a list so
    // the legacy A4 rendering below can keep iterating.
    const passengers: Array<{ name: string; phone: string; seatNumber: number }> = [
      {
        name: booking.passengerName,
        phone: booking.passengerPhone,
        seatNumber: booking.seatNumber,
      },
    ];

    setPdfLoading(true);
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 15;
      const contentWidth = pageWidth - margin * 2;

      // Colors
      const primaryColor: [number, number, number] = [0, 140, 137]; // #008C89
      const darkColor: [number, number, number] = [26, 26, 46]; // #1A1A2E
      const grayColor: [number, number, number] = [127, 140, 141]; // #7F8C8D

      let y = margin;

      // ========== HEADER ==========
      pdf.setFillColor(...primaryColor);
      pdf.roundedRect(margin, y, contentWidth, 35, 3, 3, 'F');

      // Logo text
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Safaro', margin + 8, y + 14);

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('AVTOBUS CHIPTASI', margin + 8, y + 22);

      // Booking code badge
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(pageWidth - margin - 55, y + 6, 50, 22, 2, 2, 'F');
      pdf.setTextColor(...grayColor);
      pdf.setFontSize(7);
      pdf.text('BUYURTMA KODI', pageWidth - margin - 50, y + 13);
      pdf.setTextColor(...darkColor);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(booking.bookingCode, pageWidth - margin - 50, y + 22);

      y += 45;

      // ========== ROUTE CARD ==========
      pdf.setFillColor(...primaryColor);
      pdf.roundedRect(margin, y, contentWidth, 50, 3, 3, 'F');

      // Date badge centered
      const dateText = new Date(booking.busTrip?.departureTime || '').toLocaleDateString('uz-UZ', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        weekday: 'long',
      });
      pdf.setFillColor(255, 255, 255, 0.2);
      const dateWidth = pdf.getTextWidth(dateText) + 16;
      pdf.roundedRect((pageWidth - dateWidth) / 2, y + 5, dateWidth, 10, 2, 2, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text(dateText, pageWidth / 2, y + 12, { align: 'center' });

      // Departure
      pdf.setTextColor(255, 255, 255, 0.7);
      pdf.setFontSize(8);
      pdf.text("JO'NASH", margin + 10, y + 22);
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      const depTime = new Date(booking.busTrip?.departureTime || '').toLocaleTimeString('uz-UZ', {
        hour: '2-digit',
        minute: '2-digit',
      });
      pdf.text(depTime, margin + 10, y + 33);
      pdf.setFontSize(11);
      pdf.text(booking.busTrip?.departureCity?.nameUz || '', margin + 10, y + 42);

      // Arrow in middle
      const centerX = pageWidth / 2;
      pdf.setDrawColor(255, 255, 255);
      pdf.setLineWidth(0.5);
      pdf.line(centerX - 20, y + 32, centerX + 15, y + 32);
      pdf.setFillColor(255, 255, 255);
      pdf.triangle(centerX + 15, y + 32, centerX + 10, y + 29, centerX + 10, y + 35, 'F');

      // Arrival
      pdf.setTextColor(255, 255, 255, 0.7);
      pdf.setFontSize(8);
      pdf.text('YETIB BORISH', pageWidth - margin - 10, y + 22, { align: 'right' });
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      const arrTime = new Date(booking.busTrip?.arrivalTime || '').toLocaleTimeString('uz-UZ', {
        hour: '2-digit',
        minute: '2-digit',
      });
      pdf.text(arrTime, pageWidth - margin - 10, y + 33, { align: 'right' });
      pdf.setFontSize(11);
      pdf.text(booking.busTrip?.arrivalCity?.nameUz || '', pageWidth - margin - 10, y + 42, { align: 'right' });

      y += 60;

      // ========== INFO ROW ==========
      pdf.setFillColor(245, 247, 250);
      pdf.roundedRect(margin, y, contentWidth, 20, 2, 2, 'F');

      const infoColWidth = contentWidth / 4;

      // Seats
      pdf.setTextColor(...grayColor);
      pdf.setFontSize(8);
      pdf.text("O'RINDIQLAR", margin + 8, y + 8);
      pdf.setTextColor(...darkColor);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text(String(booking.seatNumber), margin + 8, y + 15);

      // Bus company
      pdf.setTextColor(...grayColor);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text('KOMPANIYA', margin + infoColWidth + 8, y + 8);
      pdf.setTextColor(...darkColor);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text(booking.busTrip?.busCompany?.name || '-', margin + infoColWidth + 8, y + 15);

      // Bus number
      pdf.setTextColor(...grayColor);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text('AVTOBUS', margin + infoColWidth * 2 + 8, y + 8);
      pdf.setTextColor(...darkColor);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text(booking.busTrip?.busNumber || '-', margin + infoColWidth * 2 + 8, y + 15);

      // Bus type
      pdf.setTextColor(...grayColor);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text('TURI', margin + infoColWidth * 3 + 8, y + 8);
      pdf.setTextColor(...darkColor);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      const busTypeLabel = { STANDARD: 'Standart', VIP: 'VIP', SLEEPER: 'Sleeper' }[booking.busTrip?.busType || 'STANDARD'] || 'Standart';
      pdf.text(busTypeLabel, margin + infoColWidth * 3 + 8, y + 15);

      y += 30;

      // ========== PASSENGERS & QR SECTION ==========
      const passengersWidth = contentWidth * 0.6;
      const qrWidth = contentWidth * 0.35;

      // Passengers header
      pdf.setFillColor(...primaryColor);
      pdf.rect(margin, y, 4, 12, 'F');
      pdf.setTextColor(...darkColor);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text("YO'LOVCHILAR", margin + 10, y + 8);

      y += 18;

      // Passenger cards
      passengers.forEach((p, index) => {
        pdf.setFillColor(245, 247, 250);
        pdf.roundedRect(margin, y, passengersWidth, 18, 2, 2, 'F');

        // Number badge
        pdf.setFillColor(...primaryColor);
        pdf.roundedRect(margin + 4, y + 3, 12, 12, 2, 2, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text(String(index + 1), margin + 10, y + 11, { align: 'center' });

        // Name and phone
        pdf.setTextColor(...darkColor);
        pdf.setFontSize(11);
        pdf.text(p.name, margin + 22, y + 8);
        pdf.setTextColor(...grayColor);
        pdf.setFontSize(9);
        pdf.text(p.phone, margin + 22, y + 14);

        // Seat badge
        pdf.setFillColor(...darkColor);
        pdf.roundedRect(margin + passengersWidth - 35, y + 4, 30, 10, 2, 2, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`O'rin ${p.seatNumber}`, margin + passengersWidth - 20, y + 11, { align: 'center' });

        y += 22;
      });

      // QR Code section
      const qrY = y - (passengers.length * 22) - 18;
      const qrX = margin + passengersWidth + 10;

      pdf.setFillColor(255, 255, 255);
      pdf.setDrawColor(230, 230, 230);
      pdf.roundedRect(qrX, qrY, qrWidth, qrWidth + 20, 3, 3, 'FD');

      // Booking code label
      pdf.setTextColor(...grayColor);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text('BUYURTMA KODI', qrX + qrWidth / 2, qrY + 10, { align: 'center' });
      pdf.setTextColor(...darkColor);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(booking.bookingCode, qrX + qrWidth / 2, qrY + 20, { align: 'center' });

      // QR code image
      if (qrDataUrl) {
        const qrSize = 40;
        pdf.addImage(qrDataUrl, 'PNG', qrX + (qrWidth - qrSize) / 2, qrY + 26, qrSize, qrSize);
      }

      // Scan label
      pdf.setTextColor(...grayColor);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Chiptani tekshirish uchun', qrX + qrWidth / 2, qrY + qrWidth + 10, { align: 'center' });
      pdf.text('QR kodni skanerlang', qrX + qrWidth / 2, qrY + qrWidth + 15, { align: 'center' });

      // Adjust y position after QR section
      y = Math.max(y, qrY + qrWidth + 25);

      // ========== PRICE SECTION ==========
      y += 5;
      pdf.setFillColor(...primaryColor);
      pdf.roundedRect(margin, y, contentWidth, 25, 3, 3, 'F');

      pdf.setTextColor(255, 255, 255, 0.8);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text("JAMI TO'LANDI", margin + 10, y + 10);

      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      const priceText = new Intl.NumberFormat('uz-UZ').format(booking.price || 0) + " so'm";
      pdf.text(priceText, margin + 10, y + 20);

      // Payment status badge
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(pageWidth - margin - 40, y + 7, 35, 12, 2, 2, 'F');
      pdf.setTextColor(...primaryColor);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text(booking.isPaid ? "TO'LANGAN" : "TO'LANMAGAN", pageWidth - margin - 22, y + 15, { align: 'center' });

      y += 35;

      // ========== FOOTER ==========
      pdf.setFillColor(245, 247, 250);
      pdf.rect(margin, y, contentWidth, 15, 'F');

      pdf.setTextColor(...grayColor);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text("Chiqishda haydovchiga QR kodni ko'rsating", margin + 5, y + 9);
      pdf.setTextColor(...primaryColor);
      pdf.setFont('helvetica', 'bold');
      pdf.text('safaro.uz', pageWidth - margin - 5, y + 9, { align: 'right' });

      const fileName = `chipta-a4-${booking.bookingCode}.pdf`;
      pdf.save(fileName);
      message.success('Chipta yuklab olindi');
    } catch (error) {
      console.error('PDF generation error:', error);
      message.error('PDF yaratishda xatolik');
    } finally {
      setPdfLoading(false);
    }
  };

  /**
   * Print the booking on an Xprinter thermal printer (80mm format).
   * Uses the shared bus-ticket-pdf generator so the output matches
   * what kassir prints at booking-create time.
   */
  const handlePrintThermal = async () => {
    if (!booking) return;
    setPdfLoading(true);
    try {
      const { generateBusTicketsPdf } = await import('../../lib/bus-ticket-pdf');
      const trip = booking.busTrip;
      const stationName = (s: BusBooking['busTrip']['departureBusStation']) =>
        s && typeof s === 'object' ? s.nameUz : (s ?? undefined) ?? undefined;
      await generateBusTicketsPdf(
        [
          {
            bookingCode: booking.bookingCode,
            groupCode: booking.groupCode,
            busCompanyName: trip.busCompany?.name,
            busNumber: trip.busNumber,
            passengerName: booking.passengerName,
            passengerPhone: booking.passengerPhone,
            seatNumber: booking.seatNumber,
            price: booking.price,
            paymentMethod: booking.paymentMethod,
            isPaid: booking.isPaid,
            boarding: {
              cityName:
                booking.boardingStop?.city?.nameUz ?? trip.departureCity.nameUz,
              stationName: stationName(trip.departureBusStation),
              scheduledTime: trip.departureTime,
            },
            disembark: {
              cityName:
                booking.disembarkStop?.city?.nameUz ?? trip.arrivalCity.nameUz,
              stationName: stationName(trip.arrivalBusStation),
              scheduledTime: trip.arrivalTime,
            },
          },
        ],
        { autoPrint: true },
      );
    } catch (err) {
      console.error(err);
      message.error('Xprinter PDF yaratishda xatolik');
    } finally {
      setPdfLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + " so'm";
  };

  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString('uz-UZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const statusColors: Record<string, string> = {
    PENDING: 'orange',
    CONFIRMED: 'blue',
    CANCELLED: 'red',
    COMPLETED: 'green',
  };

  const statusLabels: Record<string, string> = {
    PENDING: 'Kutilmoqda',
    CONFIRMED: 'Tasdiqlangan',
    CANCELLED: 'Bekor qilingan',
    COMPLETED: 'Yakunlangan',
  };

  const busTypeLabels: Record<string, string> = {
    STANDARD: 'Standart',
    VIP: 'VIP',
    SLEEPER: 'Sleeper',
  };

  const passengerColumns = [
    {
      title: "O'rindiq",
      dataIndex: 'seatNumber',
      key: 'seatNumber',
      render: (seat: number) => <Tag color="blue">{seat}</Tag>,
    },
    {
      title: 'Ism',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Passport',
      dataIndex: 'passport',
      key: 'passport',
      render: (passport: string) => passport || '-',
    },
  ];

  const canCancel = booking?.status === 'PENDING' || booking?.status === 'CONFIRMED';
  const canConfirmPayment = !booking?.isPaid && booking?.status !== 'CANCELLED';

  return (
    <Show
      isLoading={isLoading}
      title="Avtobus buyurtmasi"
      headerButtons={
        <Space>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'thermal',
                  label: 'Xprinter (80mm) chop etish',
                  icon: <PrinterOutlined />,
                  onClick: handlePrintThermal,
                },
                {
                  key: 'a4-download',
                  label: 'A4 chiptani yuklab olish',
                  icon: <DownloadOutlined />,
                  onClick: handleDownloadPDF,
                },
              ],
            }}
          >
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              loading={pdfLoading}
              style={{ background: '#008C89' }}
            >
              Chop etish
            </Button>
          </Dropdown>
          {canConfirmPayment && (
            <Popconfirm
              title="To'lovni tasdiqlaysizmi?"
              description={
                <Space direction="vertical" size="small" style={{ marginTop: 8 }}>
                  <span>To'lov turi:</span>
                  <Space>
                    <Button size="small" onClick={() => handleConfirmPayment('CASH')}>
                      Naqd
                    </Button>
                    <Button size="small" onClick={() => handleConfirmPayment('CARD')}>
                      Karta
                    </Button>
                  </Space>
                </Space>
              }
              showCancel={false}
              okButtonProps={{ style: { display: 'none' } }}
            >
              <Button type="primary" icon={<CheckOutlined />}>
                To'lovni tasdiqlash
              </Button>
            </Popconfirm>
          )}
          {canCancel && (
            <Popconfirm
              title="Buyurtmani bekor qilasizmi?"
              description="O'rindiqlar bo'shatiladi"
              onConfirm={handleCancel}
            >
              <Button danger icon={<CloseOutlined />}>
                Bekor qilish
              </Button>
            </Popconfirm>
          )}
          <Button onClick={() => list('admin/bus/bookings')}>
            Orqaga
          </Button>
        </Space>
      }
    >
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={8}>
          <Card>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <QRCode value={booking?.bookingCode || ''} size={150} />
              <div style={{ marginTop: 8 }}>
                <Tag color="blue" style={{ fontSize: 16, padding: '4px 12px' }}>
                  {booking?.bookingCode}
                </Tag>
              </div>
            </div>

            <Descriptions column={1} size="small">
              <Descriptions.Item label="Holat">
                <Tag color={statusColors[booking?.status || 'PENDING']}>
                  {statusLabels[booking?.status || 'PENDING']}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="To'lov">
                <Tag color={booking?.isPaid ? 'green' : 'orange'}>
                  {booking?.isPaid ? "To'langan" : "To'lanmagan"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Summa">
                {formatCurrency(booking?.price || 0)}
              </Descriptions.Item>
              <Descriptions.Item label="Sana">
                {formatDateTime(booking?.createdAt || '')}
              </Descriptions.Item>
            </Descriptions>

            {booking?.cancelledAt && (
              <div style={{ marginTop: 16, padding: 12, background: '#fff2f0', borderRadius: 8 }}>
                <div style={{ color: '#cf1322', fontWeight: 500 }}>Bekor qilingan</div>
                <div style={{ fontSize: 12 }}>{formatDateTime(booking.cancelledAt)}</div>
                {booking.cancelReason && (
                  <div style={{ marginTop: 8, fontSize: 12 }}>
                    Sabab: {booking.cancelReason}
                  </div>
                )}
              </div>
            )}
          </Card>

          <Card title="Mijoz ma'lumotlari" style={{ marginTop: 24 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Ism">{booking?.passengerName}</Descriptions.Item>
              <Descriptions.Item label="Telefon">{booking?.passengerPhone}</Descriptions.Item>
              <Descriptions.Item label="Email">{booking?.passengerEmail || '-'}</Descriptions.Item>
              <Descriptions.Item label="Pasport">{booking?.passengerPassport || '-'}</Descriptions.Item>
              <Descriptions.Item label="Manba">
                <Tag color={booking?.source === 'KASSA' ? 'purple' : 'cyan'}>
                  {booking?.source === 'KASSA' ? 'Kassa' : 'Program'}
                </Tag>
              </Descriptions.Item>
              {booking?.paymentMethod && (
                <Descriptions.Item label="To'lov turi">
                  <Tag color={booking.paymentMethod === 'CASH' ? 'green' : 'blue'}>
                    {booking.paymentMethod === 'CASH' ? 'Naqd' : 'Karta'}
                  </Tag>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Card title="Reys ma'lumotlari">
            <Row gutter={[24, 16]}>
              <Col xs={24} md={12}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ color: '#7F8C8D', fontSize: 12 }}>Jo'nash</div>
                  <div style={{ fontWeight: 500, fontSize: 16 }}>
                    {booking?.busTrip?.departureCity?.nameUz}
                  </div>
                  <div style={{ fontSize: 12 }}>{booking?.busTrip?.departureRegion?.nameUz}</div>
                  <div style={{ fontSize: 12 }}>
                    {typeof booking?.busTrip?.departureBusStation === 'object'
                      ? booking?.busTrip?.departureBusStation?.nameUz
                      : booking?.busTrip?.departureBusStation || ''}
                  </div>
                  <div style={{ color: '#1890ff', marginTop: 4 }}>
                    {formatDateTime(booking?.busTrip?.departureTime || '')}
                  </div>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ color: '#7F8C8D', fontSize: 12 }}>Yetib borish</div>
                  <div style={{ fontWeight: 500, fontSize: 16 }}>
                    {booking?.busTrip?.arrivalCity?.nameUz}
                  </div>
                  <div style={{ fontSize: 12 }}>{booking?.busTrip?.arrivalRegion?.nameUz}</div>
                  <div style={{ fontSize: 12 }}>
                    {typeof booking?.busTrip?.arrivalBusStation === 'object'
                      ? booking?.busTrip?.arrivalBusStation?.nameUz
                      : booking?.busTrip?.arrivalBusStation || ''}
                  </div>
                  <div style={{ color: '#52c41a', marginTop: 4 }}>
                    {formatDateTime(booking?.busTrip?.arrivalTime || '')}
                  </div>
                </div>
              </Col>
            </Row>

            <Descriptions column={2} size="small" style={{ marginTop: 16 }}>
              <Descriptions.Item label="Kompaniya">
                <a onClick={() => show('admin/bus/companies', booking?.busTrip?.busCompany?.id || 0)}>
                  {booking?.busTrip?.busCompany?.name}
                </a>
              </Descriptions.Item>
              <Descriptions.Item label="Avtobus turi">
                {busTypeLabels[booking?.busTrip?.busType || 'STANDARD']}
              </Descriptions.Item>
              <Descriptions.Item label="Avtobus raqami">
                {booking?.busTrip?.busNumber || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="O'rindiq narxi">
                {formatCurrency(booking?.busTrip?.pricePerSeat || 0)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Yo'lovchi" style={{ marginTop: 24 }}>
            <Table
              dataSource={booking ? [{
                seatNumber: booking.seatNumber,
                name: booking.passengerName,
                phone: booking.passengerPhone,
                passport: booking.passengerPassport,
              }] : []}
              columns={passengerColumns}
              rowKey="seatNumber"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </Show>
  );
};
